import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ConsultationModal = ({ isOpen, onClose, type = 'consultation' }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/api/consultation`, {
        ...formData,
        type: type
      });

      if (response.data.success) {
        toast({
          title: "Request Submitted Successfully",
          description: "We'll contact you within 24 hours to discuss your requirements.",
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        onClose();
      }
    } catch (error) {
      console.error('Error submitting consultation:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "An error occurred. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="consultation-modal-overlay">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="relative bg-white border border-gray-200 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="consultation-modal">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-black">
            {type === 'consultation' ? 'Request a Consultation' : 'Discuss a Vacancy'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-600 hover:text-black transition-colors duration-200"
            data-testid="modal-close-btn"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="consultation-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name" className="text-black mb-2 block">
                Full Name *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-gray-50 border-gray-200 text-black focus:border-[#FFD000]"
                placeholder="John Smith"
                data-testid="input-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-black mb-2 block">
                Email Address *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-gray-50 border-gray-200 text-black focus:border-[#FFD000]"
                placeholder="john@company.com"
                data-testid="input-email"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="phone" className="text-black mb-2 block">
                Phone Number *
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                className="bg-gray-50 border-gray-200 text-black focus:border-[#FFD000]"
                placeholder="+27 XX XXX XXXX"
                data-testid="input-phone"
              />
            </div>

            <div>
              <Label htmlFor="company" className="text-black mb-2 block">
                Company / Establishment *
              </Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="bg-gray-50 border-gray-200 text-black focus:border-[#FFD000]"
                placeholder="Restaurant / Hotel Name"
                data-testid="input-company"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="message" className="text-black mb-2 block">
              {type === 'consultation' ? 'Tell us about your hiring challenges' : 'Vacancy Details'} *
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              className="bg-gray-50 border-gray-200 text-black focus:border-[#FFD000] resize-none"
              placeholder={type === 'consultation' 
                ? "Describe your current hiring challenges, turnover issues, or operational concerns..."
                : "Role title, key responsibilities, experience requirements, and any specific challenges..."
              }
              data-testid="input-message"
            />
          </div>

          <div className="bg-[#FFD000]/10 border border-[#FFD000]/30 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              All consultations are confidential. We'll review your requirements and contact you within 24 hours to discuss how structured screening can address your specific needs.
            </p>
          </div>

          <div className="flex space-x-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#FFD000] hover:bg-[#FFD000]/90 text-black font-semibold py-6"
              data-testid="submit-request-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="border-gray-200 text-gray-700 hover:bg-gray-100"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsultationModal;
